import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Stack,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Avatar,
  Chip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Menu,
  MenuItem as MUIMenuItem,
  useTheme,
  useMediaQuery,
  Grid,
  Tooltip,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { MdDelete, MdAddTask } from "react-icons/md";

import { useDispatch, useSelector } from "react-redux";
import { VendorDashboardAction } from "../../../Redux/action/VendorDashboardAction";
import Pagination from "../../Common/Pagination/Pagination";
import noImage from "../../../images/dummy_dp.png";
import {
  ActivateVendor,
  DeactivateVendor,
} from "../../../Services/vendorServices";
import { useLocation, useNavigate } from "react-router-dom";
import useDebounce from "../../../Hooks/useDebounce";
import dayjs from "dayjs";

const VendorPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  // --- States ---
  const [pageNumber, setPageNumber] = useState(0);
  const [size] = useState(10);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  const [activeTab, setActiveTab] = useState("ALL");
  const [filterActive, setFilterActive] = useState("ALL");

  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [activateDialog, setActivateDialog] = useState(false);
  const [deactivateDialog, setDeactivateDialog] = useState(false);
  const [isLoadingAction, setIsLoadingAction] = useState(false);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuVendorId, setMenuVendorId] = useState(null);

  const menuOpen = Boolean(menuAnchor);

  // Redux Data
  const loader = useSelector((s) => s?.VendorSideDashboard?.loading);
  const vendorData = useSelector(
    (s) => s?.VendorSideDashboard?.result?.content || []
  );
  const totalPages = useSelector(
    (s) => s?.VendorSideDashboard?.result?.totalPages
  );

  // Fetch data
  useEffect(() => {
    dispatch(
      VendorDashboardAction({
        pageNumber,
        size,
        search: debouncedSearch,
        activeTab,
        filterActive,
      })
    );
  }, [pageNumber, debouncedSearch, activeTab, filterActive]);
  useEffect(() => setPageNumber(0), [debouncedSearch]);

  const handleTabChange = (_, value) => {
    setActiveTab(value);
    setPageNumber(0);
  };

  const openMenu = (e, id) => {
    setMenuAnchor(e.currentTarget);
    setMenuVendorId(id);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuVendorId(null);
  };

  const handleActivate = async () => {
    setIsLoadingAction(true);
    await ActivateVendor(selectedVendorId);
    dispatch(
      VendorDashboardAction({
        pageNumber,
        size,
        search: debouncedSearch,
        activeTab,
        filterActive,
      })
    );
    setIsLoadingAction(false);
    setActivateDialog(false);
    setSelectedVendorId(null);
  };

  const handleDeactivate = async () => {
    setIsLoadingAction(true);
    await DeactivateVendor(selectedVendorId);
    dispatch(
      VendorDashboardAction({
        pageNumber,
        size,
        search: debouncedSearch,
        activeTab,
        filterActive,
      })
    );
    setIsLoadingAction(false);
    setDeactivateDialog(false);
    setSelectedVendorId(null);
  };

  const VerifiedChip = ({ verified }) =>
    verified ? (
      <Chip
        icon={<CheckCircleIcon color="#2e7d32" />}
        label="Verified"
        size="small"
        sx={{
          bgcolor: "#e6f4ea",
          color: "#2e7d32",
          fontWeight: 600,
          borderRadius: "6px",
        }}
      />
    ) : (
      <Chip
        icon={<CancelIcon color="#e65100" />}
        label="Unverified"
        size="small"
        sx={{
          bgcolor: "#fff4e6",
          color: "#e65100",
          fontWeight: 600,
          borderRadius: "6px",
        }}
      />
    );

  const StatusChip = ({ active }) =>
    active ? (
      <Chip
        icon={<CheckCircleIcon color="#1b5e20" />}
        label="Active"
        size="small"
        sx={{
          bgcolor: "#e6f4ea",
          color: "#1b5e20",
          fontWeight: 600,
          borderRadius: "6px",
        }}
      />
    ) : (
      <Chip
        icon={<CancelIcon color="#c62828" />}
        label="Inactive"
        size="small"
        sx={{
          bgcolor: "#fdecea",
          color: "#c62828",
          fontWeight: 600,
          borderRadius: "6px",
        }}
      />
    );

  const cell = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    padding: "10px 16px",
    fontSize: 14,
  };

  const formatCustomDate = (dateStr) => {
  if (!dateStr) return "—";

  // "31-12-2025 12:07:31 IST"
  const [datePart, timePart] = dateStr.split(" ");
  if (!datePart || !timePart) return "—";

  const [day, month, year] = datePart.split("-");
  const isoLikeDate = `${year}-${month}-${day}T${timePart}`;

  const date = new Date(isoLikeDate);
  if (isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  })
  .format(date)
  .replace("am", "AM")
  .replace("pm", "PM");;
};


  return (
    <Box sx={{ bgcolor: "#eef5fa", minHeight: "100vh", p: 3 }}>
      <Box sx={{ maxWidth: 1300, mx: "auto" }}>
        {/* --- HEADER --- */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            border: "1px solid rgba(45,109,150,0.25)",
            background:
              "linear-gradient(180deg, rgba(45,109,150,0.08), #ffffff 70%)",
            position: "sticky",
            top: 0,
            zIndex: 10,
            backdropFilter: "blur(6px)",
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, mb: 2, color: "#2d6d96" }}
          >
            Vendor Management
          </Typography>

          <Grid container spacing={2}>
            {/* Verification Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Verification Status</InputLabel>
                <Select
                  label="Verification Status"
                  value={activeTab}
                  onChange={(e) => setActiveTab(e.target.value)}
                >
                  <MenuItem value="ALL">All</MenuItem>
                  <MenuItem value="VERIFIED">Verified</MenuItem>
                  <MenuItem value="NOT_VERIFIED">Not Verified</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Account Status */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Account Status</InputLabel>
                <Select
                  label="Account Status"
                  value={filterActive}
                  onChange={(e) => setFilterActive(e.target.value)}
                >
                  <MenuItem value="ALL">All</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Search */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search vendors..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#2d6d96" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* --- TABLE CARD --- */}
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: "0px 3px 12px rgba(0,0,0,0.08)",
          }}
        >
          <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    "Company",
                    "Founder",
                    "Email",
                    "Address",
                    "Verified",
                    "Status",
                    "Last Login",
                    "Created At",
                    "Actions",
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontWeight: 700,
                        background: "#e9f2f7",
                        color: "#286a94",
                        whiteSpace: "nowrap",
                        fontSize: 13,
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loader ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <CircularProgress color="primary" />
                    </TableCell>
                  </TableRow>
                ) : vendorData.length ? (
                  vendorData.map((v) => (
                    <TableRow hover key={v.vendorId}>
                      {/* Company */}
                      <TableCell sx={cell}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar
                            src={v.companyLogoUrl || noImage}
                            alt="Company logo"
                            imgProps={{
                              loading: "eager",
                              decoding: "async",
                            }}
                            sx={{
                              width: 32,
                              height: 32,
                              flexShrink: 0,
                              bgcolor: "#fff",
                            }}
                          />

                          <Typography sx={{ fontWeight: 600 }}>
                            {v.company || "N/A"}
                          </Typography>
                        </Stack>
                      </TableCell>

                      <TableCell sx={cell}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar
                            src={v.vendorProfileImageUrl || noImage}
                            alt="Vendor profile"
                            imgProps={{
                              loading: "eager",
                              decoding: "async",
                            }}
                            sx={{
                              width: 32,
                              height: 32,
                              flexShrink: 0,
                            }}
                          />

                          <Tooltip
                            title={
                              `${v?.firstName ?? ""} ${
                                v?.lastName ?? ""
                              }`.trim() || "N/A"
                            }
                            arrow
                          >
                            <Typography
                              sx={{
                                maxWidth: 160,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                cursor: "pointer",
                              }}
                            >
                              {`${v?.firstName?.slice(0, 15) ?? ""} ${
                                v?.lastName?.slice(0, 15) ?? ""
                              }`.trim() || "N/A"}
                            </Typography>
                          </Tooltip>
                        </Stack>
                      </TableCell>

                      <TableCell sx={cell}>{v.email}</TableCell>
                      <TableCell sx={cell}>
                        {v.locations?.[0]?.state || "N/A"}
                      </TableCell>

                      <TableCell sx={cell}>
                        <VerifiedChip verified={v.isAccountVerified} />
                      </TableCell>

                      <TableCell sx={cell}>
                        <StatusChip active={v.status} />
                      </TableCell>
                       <TableCell sx={cell}>
                        {v?.lastLogin
                          ? formatCustomDate(v.lastLogin)
                          : "—"}
                      </TableCell>

                      <TableCell sx={cell}>
                        {v?.createdAt
                          ? dayjs(v.createdAt).format("DD MMM YYYY, hh:mm A")
                          : "—"}
                      </TableCell>

                      <TableCell sx={{ ...cell, textAlign: "center" }}>
                        <IconButton onClick={(e) => openMenu(e, v.vendorId)}>
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                      sx={{ py: 4, fontSize: 16, fontWeight: 600 }}
                    >
                      No Vendors Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
            <Pagination
              totalPages={totalPages}
              currentPage={pageNumber}
              onPageChange={setPageNumber}
            />
          </Box>
        </Card>
      </Box>

      {/* --- Menu --- */}
      <Menu anchorEl={menuAnchor} open={menuOpen} onClose={closeMenu}>
        <MUIMenuItem
          onClick={() =>
            navigate(`/admin/vendor/view?vendorId=${menuVendorId}`)
          }
        >
          <VisibilityIcon sx={{ mr: 1, fontSize: 18 }} /> View
        </MUIMenuItem>

        {vendorData.find((x) => x.vendorId === menuVendorId)?.status ? (
          <MUIMenuItem
            onClick={() => {
              setDeactivateDialog(true);
              setSelectedVendorId(menuVendorId);
              closeMenu();
            }}
          >
            <MdDelete size={18} style={{ marginRight: 8 }} /> Deactivate
          </MUIMenuItem>
        ) : (
          <MUIMenuItem
            onClick={() => {
              setActivateDialog(true);
              setSelectedVendorId(menuVendorId);
              closeMenu();
            }}
          >
            <MdAddTask size={18} style={{ marginRight: 8 }} /> Activate
          </MUIMenuItem>
        )}
      </Menu>

      {/* --- Dialogs --- */}
      <Dialog open={activateDialog} onClose={() => setActivateDialog(false)}>
        <DialogTitle>Activate Vendor</DialogTitle>
        <DialogContent>
          Are you sure you want to activate this vendor?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateDialog(false)} variant="outlined">
            No
          </Button>
          <Button onClick={handleActivate} variant="contained">
            {isLoadingAction ? <CircularProgress size={20} /> : "Yes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deactivateDialog}
        onClose={() => setDeactivateDialog(false)}
      >
        <DialogTitle>Deactivate Vendor</DialogTitle>
        <DialogContent>
          Are you sure you want to deactivate this vendor?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDialog(false)} variant="outlined">
            No
          </Button>
          <Button onClick={handleDeactivate} variant="contained" color="error">
            {isLoadingAction ? <CircularProgress size={20} /> : "Yes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorPage;
